import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

import { logError } from '@/lib/sentry';
import { isStaleDepositError, isSettledOrderDepositError } from '@/supabase/deposit/depositErrors';
import * as depositService from '@/supabase/deposit/service/depositService';
import type { DepositQueue } from '@/supabase/deposit/service/depositService';
import type {
  DepositListItem,
  DepositMatch,
  OrderCandidate,
  ParsedDeposit,
  RescueRevalidation,
} from '@/supabase/deposit/types/deposit';
import { useAuth } from '../contexts/AuthContext';

const EMPTY: DepositQueue = { actionNeeded: [], excluded: [], confirmed: [] };
const REFRESH_ERROR_TOAST_ID = 'deposits-refresh-error';

// 구제 확정/원복 경고 토스트의 금액 표기(스펙 §5 카피와 동일 포맷).
const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

// 구제 확정 결과(스펙 §5): 'ok'=확정+배송비 기록 완료, 'shipping-unrecorded'=확정됐으나 Step 2 미반영(경고),
// 'withdrawn'=제출 시 재검증 탈락(아무것도 쓰지 않음, 철회). Step 1 하드 실패는 throw(기존 에러 UX).
type RescueConfirmOutcome = 'ok' | 'shipping-unrecorded' | 'withdrawn';

type RefreshResult = 'updated' | 'failed' | 'superseded';

function allItems(queue: DepositQueue): DepositListItem[] {
  return [...queue.actionNeeded, ...queue.excluded, ...queue.confirmed];
}

function removeItem(queue: DepositQueue, matchId: number): DepositQueue {
  return {
    actionNeeded: queue.actionNeeded.filter((item) => item.match.id !== matchId),
    excluded: queue.excluded.filter((item) => item.match.id !== matchId),
    confirmed: queue.confirmed.filter((item) => item.match.id !== matchId),
  };
}

function moveItemToExcluded(
  queue: DepositQueue,
  matchId: number,
  updatedMatch: DepositMatch,
): DepositQueue {
  const item = allItems(queue).find((candidate) => candidate.match.id === matchId);
  if (!item) return queue;

  const withoutItem = removeItem(queue, matchId);
  const excludedItem: DepositListItem = {
    ...item,
    match: updatedMatch,
    uiStatus: 'excluded',
    candidates: [],
    confirmedOrder: null,
  };

  return {
    ...withoutItem,
    excluded: [excludedItem, ...withoutItem.excluded],
  };
}

function moveItemToConfirmed(
  queue: DepositQueue,
  matchId: number,
  updatedMatch: DepositMatch,
  confirmedOrder: OrderCandidate,
): DepositQueue {
  const item = allItems(queue).find((candidate) => candidate.match.id === matchId);
  if (!item) return queue;

  const withoutItem = removeItem(queue, matchId);
  const confirmedItem: DepositListItem = {
    ...item,
    match: updatedMatch,
    uiStatus: 'confirmed',
    candidates: [],
    confirmedOrder,
  };

  return {
    ...withoutItem,
    confirmed: [confirmedItem, ...withoutItem.confirmed],
  };
}

function queuesEqual(left: DepositQueue, right: DepositQueue): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function useDeposits() {
  const { user, isAuthenticating } = useAuth();
  const userId = user?.id ?? null;
  const [queue, setQueue] = useState<DepositQueue>(EMPTY);
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [errorUserId, setErrorUserId] = useState<string | null>(null);
  const [queueVersion, setQueueVersion] = useState(0);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const loadedUserIdRef = useRef(loadedUserId);
  loadedUserIdRef.current = loadedUserId;
  const requestSequenceRef = useRef(0);
  const refreshRef = useRef<() => Promise<RefreshResult>>(async () => 'superseded');

  const refresh = useCallback(async (): Promise<RefreshResult> => {
    if (!userId) return 'superseded';

    const requestUserId = userId;
    const sequence = ++requestSequenceRef.current;
    const isBlockingLoad = loadedUserIdRef.current !== requestUserId;

    if (isBlockingLoad) {
      setLoading(true);
      setError(null);
      setErrorUserId(null);
    }

    try {
      const nextQueue = await depositService.loadDepositQueue(requestUserId);
      const isCurrentRequest =
        sequence === requestSequenceRef.current && userIdRef.current === requestUserId;
      if (!isCurrentRequest) return 'superseded';

      const hasChanged = !queuesEqual(queueRef.current, nextQueue);
      const committedQueue = hasChanged ? nextQueue : queueRef.current;
      queueRef.current = committedQueue;
      loadedUserIdRef.current = requestUserId;
      setQueue(committedQueue);
      setLoadedUserId(requestUserId);
      setError(null);
      setErrorUserId(null);
      setLoading(false);
      if (hasChanged) setQueueVersion((version) => version + 1);
      toast.dismiss(REFRESH_ERROR_TOAST_ID);
      return 'updated';
    } catch (err) {
      const isCurrentRequest =
        sequence === requestSequenceRef.current && userIdRef.current === requestUserId;
      if (!isCurrentRequest) return 'superseded';

      logError('useDeposits.refresh', err);
      if (isBlockingLoad) {
        setError(err as Error);
        setErrorUserId(requestUserId);
        setLoading(false);
      } else {
        toast.error('입금 목록 동기화에 실패했습니다', {
          id: REFRESH_ERROR_TOAST_ID,
          description: '기존 목록은 유지됩니다.',
          duration: Infinity,
          action: {
            label: '재시도',
            onClick: () => { void refreshRef.current(); },
          },
        });
      }
      return 'failed';
    }
  }, [userId]);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!userId && !isAuthenticating) {
      requestSequenceRef.current++;
      queueRef.current = EMPTY;
      loadedUserIdRef.current = null;
      setQueue(EMPTY);
      setLoadedUserId(null);
      setError(null);
      setErrorUserId(null);
      setLoading(false);
      return;
    }
    if (userId) void refresh();
  }, [userId, isAuthenticating, refresh]);

  // 실시간: deposit_match 변경(새 입금 INSERT·확정/제외 UPDATE) → 디바운스 재조회(codexloop 증분2 R4).
  // nav 배지는 실시간인데 페이지가 정적이면 페이지를 보는 중 새 입금이 와도 '배지 3 / 페이지 2'
  // 안정 불일치가 생긴다. 채널명은 useDepositIncoming(/order)과 같지만 두 훅은 라우트 배타적이라
  // 동시 구독 없음. 자기 액션(확정 등)의 UPDATE도 발화하지만 refresh는 seq 가드 + queuesEqual
  // 커밋이라 추가 refetch 1회로 무해.
  useEffect(() => {
    if (!userId) return;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const onChange = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => { void refreshRef.current(); }, 400);
    };
    const unsubscribe = depositService.subscribeToDepositMatches(userId, onChange, (status, err) => {
      // SUBSCRIBED(첫 구독+재연결) 시 재조회: 초기 스냅샷과 구독 시작 사이·끊김 동안 놓친
      // 변경 보정(codexloop R5 — useDepositActionCount와 동일 패턴, seq 가드로 중복 무해).
      if (status === 'SUBSCRIBED') void refreshRef.current();
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logError('useDeposits.realtime', err ?? new Error(status));
      }
    });
    return () => {
      if (debounce) clearTimeout(debounce);
      unsubscribe();
    };
  }, [userId]);

  const commitQueuePatch = useCallback((update: (current: DepositQueue) => DepositQueue) => {
    const nextQueue = update(queueRef.current);
    if (nextQueue === queueRef.current || queuesEqual(nextQueue, queueRef.current)) return;
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    setQueueVersion((version) => version + 1);
  }, []);

  const confirm = useCallback(async (
    depositMatchId: number,
    order: OrderCandidate,
    expectedModifiedAt: string,
  ) => {
    if (!userId) return;
    const mutationUserId = userId;
    requestSequenceRef.current++;
    try {
      const updatedMatch = await depositService.linkMatch(
        depositMatchId,
        order.id,
        expectedModifiedAt,
        order.paidThreshold,
        order.total_price,
        order.shippingFeeOverride,
        'deposits',
        // 지문 운반 계약(스펙 §3.3): 액션 대상 행 페이로드(order)에 로드 시 부착된 지문만 전달 —
        // 전역/ref에서 읽거나 재계산하면 "임계값 A + 지문 B" 조합이 가능해진다.
        order.settingsFingerprint,
      );
      if (userIdRef.current !== mutationUserId) return;
      commitQueuePatch((current) => (
        moveItemToConfirmed(current, depositMatchId, updatedMatch, order)
      ));
    } catch (err) {
      // stale(P0003)·완납/출고 재확정 거부(P0002) 둘 다 영구 거부 — 재시도 대신 최신 스냅샷 유도.
      if ((isStaleDepositError(err) || isSettledOrderDepositError(err)) && userIdRef.current === mutationUserId) {
        await refresh();
      }
      throw err;
    }
    void refresh();
  }, [commitQueuePatch, refresh, userId]);

  const undo = useCallback(async (
    depositMatchId: number,
    expectedTotal: number | null,
    expectedPrice: number | null,
    expectedShipOverride: number | null,
    // 배송설정 지문(스펙 §3.3): 호출측(DepositRow)이 액션 대상 행 페이로드에서 꺼내 전달(rescue 행은 null 고정).
    settingsFingerprint: string | null,
    // 구제 undo 원복(스펙 §5): 호출측(DepositRow)이 pre-undo match.confirmed_source==='deposits_rescue'일 때만
    // 주입(undo 후 행은 소스가 지워지므로 사전 판정). undo RPC 성공 뒤 override CAS 원복.
    rescueRevert?: { orderId: number; presetAmount: number },
  ) => {
    if (!userId) return;
    const mutationUserId = userId;
    requestSequenceRef.current++;
    try {
      await depositService.undoMatch(depositMatchId, expectedTotal, expectedPrice, expectedShipOverride, settingsFingerprint);
    } catch (err) {
      // stale(40001 — 다른 탭이 그 사이 변경/주문 금융 편집): 같은 지문 재제출은 계속 실패하므로
      // confirm과 동일하게 큐를 refresh해 최신 스냅샷을 보게 한다(codexloop 분할 R3 P2).
      if (isStaleDepositError(err) && userIdRef.current === mutationUserId) {
        await refresh();
      }
      throw err;
    }
    if (userIdRef.current !== mutationUserId) return;
    // undo 성공 후 override 원복(스펙 §5). undo RPC가 주문을 '미지정'으로 되돌린 뒤라야 CAS 조건이 맞는다.
    if (rescueRevert) {
      try {
        // affected 0(동시 재확정·셀러 수동 변경으로 status≠미지정 or override≠preset) = 정상, 조용히 통과.
        await depositService.revertRescueShippingOverride(rescueRevert.orderId, rescueRevert.presetAmount);
      } catch {
        // 실제 에러(네트워크/RLS 등): 주문 재조회 → 여전히 미지정 ∧ override=preset이면 경고(방치 시
        // 같은 입금이 blue 정확일치로 재등장하는 피드백 루프이므로 조용히 삼키지 않는다, 스펙 §5).
        const stuck = await depositService.isRescueOverrideStuck(rescueRevert.orderId, rescueRevert.presetAmount);
        if (stuck) {
          toast.error(`되돌리기는 완료됐지만 주문 배송비(${won(rescueRevert.presetAmount)}) 원복에 실패했습니다. 정산서 화면에서 배송비를 확인해주세요.`);
        }
      }
    }
    await refresh();
  }, [refresh, userId]);

  // ── 배송비 구제 확정 체인(스펙 §5, confirm-first) ─────────────────────────────
  // 카드 클릭 시 재검증(§5.1): fresh loadDepositQueue 재판정이 같은 (orderId, presetAmount)로 유효한지.
  // DepositRow가 이 결과로 ConfirmGuard를 열거나(유효) 제안을 철회한다(null).
  const rescueRevalidate = useCallback(async (
    matchId: number, orderId: number, presetAmount: number,
  ): Promise<RescueRevalidation | null> => {
    if (!userId) return null;
    return depositService.revalidateShippingRescue(userId, matchId, orderId, presetAmount);
  }, [userId]);

  // ConfirmGuard 제출 시(§5.2 재검증) → Step 1 확정(돈) → Step 2 배송비 기록(CAS). Step 1 하드 실패는
  // throw(DepositRow가 기존 stale/settled/일시오류 UX 처리). Step 2 실패는 돈 무결이라 경고 결과만 반환.
  const rescueConfirm = useCallback(async (
    depositMatchId: number, orderId: number, presetAmount: number,
  ): Promise<RescueConfirmOutcome> => {
    if (!userId) return 'withdrawn';
    const mutationUserId = userId;
    requestSequenceRef.current++;
    // §5.2 제출 시 재검증 재실행: 모달이 열려 있는 동안의 드리프트(프리셋 삭제·기준 변경 등)는 RPC 지문이
    // 못 잡으므로 §3 재판정을 한 번 더 수행 후 통과 시에만 Step 1 진행.
    const fresh = await depositService.revalidateShippingRescue(userId, depositMatchId, orderId, presetAmount);
    if (userIdRef.current !== mutationUserId) return 'withdrawn';
    if (!fresh) { await refresh(); return 'withdrawn'; }

    // Step 1 — 확정(돈). 기존 link_deposit_manual 재사용.
    //   ★p_expected_total = fresh.expectedTotal(= total + preset)는 order.shipping_fee_override(NULL)와
    //     무관하게 서버가 완납 판정(v_sum >= p_expected_total)에 그대로 쓰는 '독립 파라미터'다. 이 계약에
    //     의존해 구제가 성립한다(스펙 게이트 i / e2e (i)가 이 계약을 못박음 — RPC 정리로 깨지면 즉시 검출).
    //   지문 = 재검증에서 관측한 舊상태(price=total_price, ship_override=NULL) → DB 대조되는 진짜 가드.
    //   실패(P0003 stale·지문 불일치·동시확정 P0002) = 아무것도 변경 안 됨(fail-closed) → throw.
    let updatedMatch: DepositMatch;
    try {
      updatedMatch = await depositService.linkMatch(
        depositMatchId, fresh.order.id, fresh.expectedModifiedAt,
        // 지문 = null 고정. ⚠deprecated(2026-07-27, 마이그 20260727220000): confirm RPC가 지문을 보지 않으므로
        // 이 null도, 서버측 rescue 배제(p_source CASE)도 이제 무동작이다. Step 1은 원장 경로라 감시 자격을
        // 받고, Step 2의 override 변경이 자동 전환 트리거를 깨워 서버 기준으로 재판정한다(§3.6 반전).
        fresh.expectedTotal, fresh.order.total_price, null, 'deposits_rescue', null,
      );
    } catch (err) {
      if ((isStaleDepositError(err) || isSettledOrderDepositError(err)) && userIdRef.current === mutationUserId) {
        await refresh();
      }
      throw err;
    }
    if (userIdRef.current !== mutationUserId) return 'ok';

    // Step 1 성공 → 낙관적 커밋(override 반영된 order로). Step 2 실패해도 확정은 유효.
    const confirmedOrder: OrderCandidate = { ...fresh.order, shippingFeeOverride: presetAmount };
    commitQueuePatch((current) => moveItemToConfirmed(current, depositMatchId, updatedMatch, confirmedOrder));

    // Step 2 — 배송비 기록(강화 CAS). 돈은 무결이라 affected 0·에러 모두 경고 결과로만 처리(throw 금지).
    let step2Ok = false;
    try {
      step2Ok = await depositService.applyRescueShippingOverride(orderId, presetAmount, fresh.order.total_price, depositMatchId);
    } catch {
      step2Ok = false;
    }
    void refresh();
    return step2Ok ? 'ok' : 'shipping-unrecorded';
  }, [commitQueuePatch, refresh, userId]);

  const reject = useCallback(async (depositMatchId: number) => {
    if (!userId) return;
    const mutationUserId = userId;
    requestSequenceRef.current++;
    const updatedMatch = await depositService.rejectMatch(depositMatchId);
    if (userIdRef.current !== mutationUserId) return;
    commitQueuePatch((current) => moveItemToExcluded(current, depositMatchId, updatedMatch));
    void refresh();
  }, [commitQueuePatch, refresh, userId]);

  const restore = useCallback(async (depositMatchId: number) => {
    if (!userId) return;
    const mutationUserId = userId;
    requestSequenceRef.current++;
    await depositService.restoreMatch(depositMatchId);
    if (userIdRef.current !== mutationUserId) return;
    await refresh();
  }, [refresh, userId]);

  const loadManualCandidates = useCallback(async (
    deposit: ParsedDeposit,
  ): Promise<OrderCandidate[]> => {
    if (!userId) return [];
    return depositService.loadManualLinkCandidates(deposit, userId);
  }, [userId]);

  const hasCurrentUserData = userId !== null && loadedUserId === userId;
  const visibleQueue = hasCurrentUserData ? queue : EMPTY;
  const visibleError = errorUserId === userId ? error : null;
  const visibleLoading = userId
    ? (!hasCurrentUserData && visibleError === null) || loading
    : loading;

  return {
    queue: visibleQueue,
    loading: visibleLoading,
    error: visibleError,
    queueVersion,
    refresh,
    confirm,
    undo,
    reject,
    restore,
    loadManualCandidates,
    rescueRevalidate,
    rescueConfirm,
  };
}
